/**
 * The ticket's attributes as a `DescriptionList`.
 *
 * Only fields that have a value are listed — a column of "Not set" tells a
 * non-technical employee nothing and pushes the things that matter off screen.
 */

import { DescriptionList, type DescriptionListEntry } from "@norican/react";
import type { ReactNode } from "react";

import { RelativeTime } from "@/components/common";
import { useLocalizedText } from "@/features/assistant";
import { useT } from "@/i18n";

import type { ActorRef, TicketDetail } from "../api/contract";
import { TicketKindBadge, TicketPriorityBadge, TicketStatusBadge } from "./ticket-badges";
import { TranslatableTextView } from "./translatable-text";

export interface TicketAttributesProps {
  ticket: TicketDetail;
}

function actorName(actor: ActorRef | null, fallback: string): ReactNode {
  return actor === null ? fallback : actor.display_name;
}

export function TicketAttributes({ ticket }: TicketAttributesProps) {
  const { t } = useT("tickets");
  const localize = useLocalizedText();

  const entries: DescriptionListEntry[] = [
    { label: t("field.number"), value: <span className="font-mono">{ticket.ticket_number}</span> },
    { label: t("field.type"), value: <TicketKindBadge kind={ticket.kind} /> },
    {
      label: t("field.status"),
      value: <TicketStatusBadge status={ticket.status} kind={ticket.kind} />,
    },
    { label: t("field.priority"), value: <TicketPriorityBadge code={ticket.priority_code} /> },
    { label: t("field.impact"), value: t(`level.${ticket.impact_code ?? "UNKNOWN"}`) },
    { label: t("field.urgency"), value: t(`level.${ticket.urgency_code ?? "UNKNOWN"}`) },
    { label: t("field.requester"), value: ticket.requester.display_name },
    { label: t("field.owner"), value: actorName(ticket.owner ?? null, t("field.unassigned")) },
    { label: t("field.created"), value: <RelativeTime value={ticket.created_at} /> },
    { label: t("field.updated"), value: <RelativeTime value={ticket.updated_at} /> },
  ];

  if (ticket.category != null) {
    // `category` is a LocalizedString, so it goes through the catalogues like
    // every other server-authored string. A tenant-specific category has no
    // key, and `useLocalizedText` then falls back to the English reference
    // rendering — which is the honest answer, and the same one every other
    // unmapped code gets.
    entries.push({ label: t("field.category"), value: localize(ticket.category) });
  }
  if (ticket.owner_team != null) {
    entries.push({ label: t("field.team"), value: ticket.owner_team });
  }
  if (ticket.service != null) {
    entries.push({ label: t("field.service"), value: ticket.service });
  }
  if (ticket.location != null) {
    entries.push({ label: t("field.location"), value: ticket.location });
  }
  if (ticket.resolved_at != null) {
    entries.push({
      label: t("field.resolved"),
      value: <RelativeTime value={ticket.resolved_at} />,
    });
  }
  if (ticket.closed_at != null) {
    entries.push({ label: t("field.closed"), value: <RelativeTime value={ticket.closed_at} /> });
  }
  if (ticket.resolution != null) {
    entries.push({
      label: t("field.resolution"),
      value: <TranslatableTextView value={ticket.resolution} multiline />,
    });
  }

  return <DescriptionList items={entries} orientation="horizontal" columns={2} />;
}
